// src/App.js
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { User, ListTodo, Send, PlusCircle, Trash2, XCircle, Info, CheckCircle } from 'lucide-react';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

// Import refactored components and utilities
import LoginScreen from './core/LoginScreen';
import ExpertInfoForm from './core/ExpertInfoForm';
import PhenomenonForm from './core/PhenomenonForm';
import { ExplanationModal, GuideModal, WelcomeModal, FieldExplanationModal, InterdependencyExplanationInputModal } from './core/Modals';
import {
  API_BASE_URL,
  toPersianDigits,
  parseCSV,
  calculateSinglePhenomenonCompletion,
  getCleanedExpertInfoPayload, // Import the new utility function
} from './core/utils';
import { csvContent, separatorBeforeIds, welcomeMessage } from './core/data';


function App() {
  const [loginState, setLoginState] = useState('initial'); // 'initial', 'expertForm', 'mainApp'
  const [nationalId, setNationalId] = useState('');
  const [expertInfo, setExpertInfo] = useState({
    firstName: '',
    lastName: '',
    province: '',
    nationalId: '',
    position: '',
    degree: '',
    specialties: '',
    education: '',
    email: '',
    mobileNumber: ''
  });
  const [phenomenaData, setPhenomenaData] = useState([]);
  const [userSuggestedPhenomena, setUserSuggestedPhenomena] = useState([]);
  const [filteredPhenomenaData, setFilteredPhenomenaData] = useState([]);
  const [phenomenaAnswers, setPhenomenaAnswers] = useState({});
  const [currentPhenomenonId, setCurrentPhenomenonId] = useState(null);
  const [showGuide, setShowGuide] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showConfirmSendModal, setShowConfirmSendModal] = useState(false);
  const [showExplanationModal, setShowExplanationModal] = useState(false);
  const [selectedPhenomenonForExplanation, setSelectedPhenomenonForExplanation] = useState(null);
  const [currentSection, setCurrentSection] = useState('expert');
  const [nextNewPhenomenonIdCounter, setNextNewPhenomenonIdCounter] = useState(1);
  const [showWelcomeModal, setShowWelcomeModal] = useState(true);
  const [showFieldExplanationModal, setShowFieldExplanationModal] = useState(false);
  const [fieldExplanationModalData, setFieldExplanationModalData] = useState(null);
  const [showInterdependencyExplanationInputModal, setShowInterdependencyExplanationInputModal] = useState(false);
  const [interdependencyExplanationInputModalData, setInterdependencyExplanationInputModalData] = useState({
    currentPhenomenonName: '',
    selectedInterdependencyNames: [],
    initialExplanation: ''
  });

  const mainContentRef = useRef(null);

  // Load phenomena data from CSV on component mount
  useEffect(() => {
    const parsedData = parseCSV(csvContent);
    setPhenomenaData(parsedData);
    if (parsedData.length > 0) {
      const firstPhenomenon = parsedData.find(p => p.type === 'phenomenon');
      if (firstPhenomenon) {
        setCurrentPhenomenonId(firstPhenomenon.id);
      }
    }
  }, []);

  // Set browser tab title
  useEffect(() => {
    document.title = "فراخوان چشم‌انداز تأمین اجتماعی";
  }, []);

  const combinedPhenomena = useMemo(() => {
    return [...phenomenaData, ...userSuggestedPhenomena];
  }, [phenomenaData, userSuggestedPhenomena]);

  // Update filtered phenomena whenever phenomenaData, userSuggestedPhenomena, or searchTerm changes
  useEffect(() => {
    const term = searchTerm.toLowerCase();
    const allPhenomena = combinedPhenomena;

    if (!term) {
      setFilteredPhenomenaData(allPhenomena);
      return;
    }

    const newFilteredData = [];
    let currentHeader = null;
    let headerAdded = false;

    allPhenomena.forEach(item => {
      if (item.type === 'main_category_header') {
        currentHeader = item;
        headerAdded = false;
      } else if (item.type === 'phenomenon') {
        if (item.name.toLowerCase().includes(term)) {
          if (currentHeader && !headerAdded) {
            newFilteredData.push(currentHeader);
            headerAdded = true;
          }
          newFilteredData.push(item);
        }
      }
    });
    setFilteredPhenomenaData(newFilteredData);
  }, [searchTerm, combinedPhenomena]);

  const fetchData = useCallback(async (id) => {
    if (!id) return;

    try {
      const expertResponse = await fetch(`${API_BASE_URL}/expert-info/${id}`);
      if (expertResponse.ok) {
        const data = await expertResponse.json();
        const cleanedData = {
          ...data,
          specialties: data.specialties || '',
          degree: data.degree || '',
          education: data.education || '',
          nationalId: id
        };
        setExpertInfo(prev => ({ ...prev, ...cleanedData }));
        setCurrentSection('phenomena');
      } else if (expertResponse.status === 404) {
        console.log("No expert info found for this national ID, user is new.");
        setLoginState('expertForm');
        setExpertInfo(prev => ({ ...prev, nationalId: id }));
        return;
      } else {
        let errorDetails = `Status: ${expertResponse.status} ${expertResponse.statusText}`;
        try {
          const errorData = await expertResponse.json();
          errorDetails += `, Message: ${errorData.message || JSON.stringify(errorData)}`;
        } catch (jsonError) {
          const rawText = await expertResponse.text();
          errorDetails += `, Raw Response: ${rawText}`;
        }
        console.error("Error fetching expert info:", errorDetails);
        toast.error(`خطا در سرور (Backend) هنگام دریافت اطلاعات کارشناس: ${errorDetails}. لطفاً مطمئن شوید اطلاعات اتصال به دیتابیس MySQL در فایل server.js صحیح است و جداول دیتابیس ایجاد شده‌اند.`);
        setLoginState('initial');
        return;
      }

      const phenomenaResponse = await fetch(`${API_BASE_URL}/phenomena-answers/${id}`);
      if (phenomenaResponse.ok) {
        const data = await phenomenaResponse.json();
        const suggestedAnswers = [];
        let maxNewId = 0;

        for (const key in data) {
            if (Object.prototype.hasOwnProperty.call(data, key)) {
                if (typeof key === 'string' && key.startsWith('new-')) {
                    suggestedAnswers.push({ id: key, name: data[key].phenomenonName || `پدیده پیشنهادی`, type: 'phenomenon', mainCategoryName: 'پدیده‌های پیشنهادی شما' });
                    const numPart = parseInt(key.split('-')[1], 10);
                    if (numPart > maxNewId) maxNewId = numPart;
                }
            }
        }
        setUserSuggestedPhenomena(suggestedAnswers);
        setNextNewPhenomenonIdCounter(maxNewId + 1);
        setPhenomenaAnswers(data);
      } else if (phenomenaResponse.status === 404) {
        console.log("No phenomena answers found for this national ID, starting fresh for phenomena.");
      } else {
        let errorDetails = `Status: ${phenomenaResponse.status} ${phenomenaResponse.statusText}`;
        try {
          const errorData = await phenomenaResponse.json();
          errorDetails += `, Message: ${errorData.message || JSON.stringify(errorData)}`;
        } catch (jsonError) {
          const rawText = await phenomenaResponse.text();
          errorDetails += `, Raw Response: ${rawText}`;
        }
        console.error("Error fetching phenomena answers:", errorDetails);
        toast.error(`خطا در سرور (Backend) هنگام دریافت پاسخ پدیده‌ها: ${errorDetails}. لطفاً مطمئن شوید اطلاعات اتصال به دیتابیس MySQL در فایل server.js صحیح است و جداول دیتابیس ایجاد شده‌اند.`);
        setLoginState('initial');
        return;
      }
      setLoginState('mainApp');
    } catch (error) {
      console.error("Network error during data fetch:", error);
      toast.error("خطا در ارتباط با سرور. لطفاً اتصال اینترنت خود را بررسی کنید و دوباره تلاش کنید.");
      setLoginState('initial');
    }
  }, []);

  useEffect(() => {
    if (nationalId && loginState !== 'initial') {
      fetchData(nationalId);
    }
  }, [nationalId, fetchData, loginState]);

  const handleExpertInfoChange = (e) => {
    const { name, value } = e.target;
    setExpertInfo(prev => ({ ...prev, [name]: value }));
  };

  const handlePhenomenonAnswerChange = (phenomenonId, field, value) => {
    setPhenomenaAnswers(prev => {
      const newAnswers = {
        ...prev,
        [phenomenonId]: {
          ...(prev[phenomenonId] || {}),
          [field]: value
        }
      };
      if (nationalId) {
        savePhenomenaAnswersToBackend(newAnswers);
      }
      return newAnswers;
    });
  };

  const savePhenomenaAnswersToBackend = async (answersToSave) => {
    try {
      const response = await fetch(`${API_BASE_URL}/phenomena-answers`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', },
        body: JSON.stringify({ nationalId: nationalId, phenomenaAnswers: answersToSave }),
      });
      if (!response.ok) {
        // *** MODIFICATION START ***
        // Specific check for Payload Too Large error
        if (response.status === 413) {
            console.error("Error saving phenomena answers: Payload too large.");
            toast.error("خطا: حجم اطلاعات وارد شده بیش از حد مجاز است. لطفاً پاسخ‌های خود را خلاصه‌تر کنید.");
            return;
        }

        // Safer general error handling
        let errorDetails;
        const errorText = await response.text(); // Read body only once
        try {
          const errorData = JSON.parse(errorText);
          errorDetails = `Status: ${response.status}, Message: ${errorData.message || errorText}`;
        } catch (jsonError) {
          errorDetails = `Status: ${response.status}, Raw Response: ${errorText}`;
        }
        // *** MODIFICATION END ***

        console.error("Error saving phenomena answers to backend:", errorDetails);
        if (response.status === 500) {
          toast.error(`خطا در سرور (Backend) هنگام ذخیره پاسخ پدیده‌ها: ${errorDetails}. لطفاً مطمئن شوید اطلاعات اتصال به دیتابیس MySQL در فایل server.js صحیح است و جداول دیتابیس ایجاد شده‌اند.`);
        } else {
          toast.error(`خطا در ذخیره پاسخ پدیده‌ها: ${errorDetails}`);
        }
      }
    } catch (error) {
      console.error("Network error saving phenomena answers:", error);
      toast.error("خطا در ارتباط با سرور هنگام ذخیره پاسخ پدیده‌ها. لطفاً اتصال اینترنت خود را بررسی کنید.");
    }
  };

  const handleSaveExpertInfo = async (infoToSave) => {
    if (!nationalId) {
      toast.error("شناسه کاربر در دسترس نیست. لطفا صفحه را رفرش کنید.");
      return { success: false, errors: { general: "شناسه کاربر در دسترس نیست." } };
    }

    const expertInfoPayload = getCleanedExpertInfoPayload(infoToSave);
    console.log("Saving expert info (payload):", { nationalId: nationalId, expertInfo: expertInfoPayload });

    try {
      const response = await fetch(`${API_BASE_URL}/expert-info`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ nationalId: nationalId, expertInfo: expertInfoPayload }),
      });

      if (response.ok) {
        toast.success("اطلاعات کارشناس با موفقیت ذخیره شد.");
        setLoginState('mainApp');
        return { success: true };
      } else {
        // *** MODIFICATION START ***
        // Specific check for Payload Too Large error
        if (response.status === 413) {
            toast.error("خطا: حجم اطلاعات وارد شده بیش از حد مجاز است. لطفاً پاسخ‌های خود را خلاصه‌تر کنید.");
            return { success: false, errors: { general: "حجم اطلاعات بیش از حد مجاز است." } };
        }
        // *** MODIFICATION END ***

        const specificErrors = {};
        let errorToLog = `Status: ${response.status}`;
        try {
          const errorData = await response.json();
          const errorMessages = [];

          if (errorData.message && errorData.message.includes("Duplicate entry")) {
            if (errorData.message.includes("for key 'email'")) {
              specificErrors.email = "این ایمیل قبلاً ثبت شده است.";
              errorMessages.push(specificErrors.email);
            }
            if (errorData.message.includes("for key 'mobile_number'")) {
              specificErrors.mobileNumber = "این شماره همراه قبلاً ثبت شده است.";
              errorMessages.push(specificErrors.mobileNumber);
            }
          }

          if (errorMessages.length > 0) {
            toast.error(errorMessages.join(' '));
          } else {
            const generalError = errorData.message || 'خطا در ذخیره اطلاعات. لطفاً ورودی خود را بررسی کرده و مجدداً تلاش کنید.';
            specificErrors.general = generalError;
            toast.error(generalError);
          }
          errorToLog = `Status: ${response.status}, Message: ${errorData.message || JSON.stringify(errorData)}`;

        } catch (jsonError) {
          const rawText = await response.text();
          errorToLog = `Status: ${response.status}, Raw Response: ${rawText}`;
          specificErrors.general = "خطای ناشناخته از سمت سرور. لطفاً بعداً تلاش کنید.";
          toast.error(specificErrors.general);
        }
        
        console.error("Error saving expert info:", errorToLog);
        return { success: false, errors: specificErrors };
      }
    } catch (error) {
      console.error("Network error saving expert info:", error);
      toast.error("خطا در ارتباط با سرور. لطفاً اتصال اینترنت خود را بررسی کنید.");
      return { success: false, errors: { general: "خطا در ارتباط با سرور." } };
    }
  };

  const handleUpdateSuggestedPhenomenonName = (id, newName) => {
    const updatedPhenomena = userSuggestedPhenomena.map(p =>
      p.id === id ? { ...p, name: newName } : p
    );
    setUserSuggestedPhenomena(updatedPhenomena);
    handlePhenomenonAnswerChange(id, 'phenomenonName', newName);
  };

  const handleDeleteSuggestedPhenomenon = (idToDelete) => {
    toast.warn(
      <div>
        <p>آیا از حذف این پدیده پیشنهادی اطمینان دارید؟ این عمل قابل بازگشت نیست.</p>
        <button
          className="bg-red-500 hover:bg-red-700 text-white font-semibold py-1 px-3 rounded-lg mt-2 ml-2"
          onClick={() => {
            toast.dismiss();
            setUserSuggestedPhenomena(prev => prev.filter(p => p.id !== idToDelete));

            setPhenomenaAnswers(prev => {
              const newAnswers = { ...prev };
              delete newAnswers[idToDelete];
              savePhenomenaAnswersToBackend(newAnswers);
              return newAnswers;
            });

            if (currentPhenomenonId === idToDelete) {
              const firstPhenomenon = combinedPhenomena.find(p => p.type === 'phenomenon' && p.id !== idToDelete);
              setCurrentPhenomenonId(firstPhenomenon ? firstPhenomenon.id : null);
            }
            toast.success("پدیده پیشنهادی حذف شد.");
          }}
        >
          بله، حذف کن
        </button>
        <button
          className="bg-gray-300 hover:bg-gray-400 text-gray-800 font-semibold py-1 px-3 rounded-lg mt-2"
          onClick={() => toast.dismiss()}
        >
          خیر
        </button>
      </div>,
      {
        autoClose: false,
        closeButton: false,
        closeOnClick: false,
        draggable: false,
      }
    );
  };

  const handleSendAll = () => {
    setShowConfirmSendModal(true);
  };

  const confirmSend = async () => {
    if (!nationalId) {
      toast.error("شناسه کاربر در دسترس نیست. لطفا صفحه را رفرش کنید.");
      setShowConfirmSendModal(false);
      return;
    }
  
    // This helper function will read the response body safely (only once)
    // and return a descriptive error string.
    const getErrorDetailsFromResponse = async (response) => {
      let errorDetails;
      const errorText = await response.text(); // Read body only once.
      try {
        const errorData = JSON.parse(errorText);
        errorDetails = `Status: ${response.status}, Message: ${errorData.message || errorText}`;
      } catch (jsonError) {
        errorDetails = `Status: ${response.status}, Raw Response: ${errorText}`;
      }
      return errorDetails;
    };
  
    try {
      const expertInfoPayload = getCleanedExpertInfoPayload(expertInfo);
  
      const expertInfoResponse = await fetch(`${API_BASE_URL}/expert-info`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ nationalId: nationalId, expertInfo: expertInfoPayload }),
      });
  
      if (!expertInfoResponse.ok) {
        // *** MODIFICATION START ***
        if (expertInfoResponse.status === 413) {
          throw new Error("حجم اطلاعات کارشناس بیش از حد مجاز است. لطفاً پاسخ‌های خود را خلاصه‌تر کنید.");
        }
        const errorDetails = await getErrorDetailsFromResponse(expertInfoResponse);
        throw new Error(`خطا در ذخیره اطلاعات کارشناس: ${errorDetails}`);
        // *** MODIFICATION END ***
      }
  
      const phenomenaAnswersResponse = await fetch(`${API_BASE_URL}/phenomena-answers`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ nationalId: nationalId, phenomenaAnswers: phenomenaAnswers }),
      });
  
      if (!phenomenaAnswersResponse.ok) {
        // *** MODIFICATION START ***
        if (phenomenaAnswersResponse.status === 413) {
          throw new Error("حجم پاسخ پدیده‌ها بیش از حد مجاز است. لطفاً پاسخ‌های خود را خلاصه‌تر کنید.");
        }
        const errorDetails = await getErrorDetailsFromResponse(phenomenaAnswersResponse);
        throw new Error(`خطا در ذخیره پاسخ پدیده‌ها: ${errorDetails}`);
        // *** MODIFICATION END ***
      }
  
      toast.success("اطلاعات با موفقیت ارسال شد!");
    } catch (error) {
      console.error("Error sending all data:", error);
      // Now the error message will be specific, like "حجم اطلاعات... بیش از حد مجاز است."
      toast.error(`خطا در ارسال اطلاعات: ${error.message}. لطفاً دوباره تلاش کنید.`);
    }
    setShowConfirmSendModal(false);
  };
  

  const cancelSend = () => {
    setShowConfirmSendModal(false);
  };

  const allActualPhenomena = useMemo(() => {
    return combinedPhenomena.filter(p => p.type === 'phenomenon');
  }, [combinedPhenomena]);

  const totalPhenomenaCount = useMemo(() => {
    return allActualPhenomena.length;
  }, [allActualPhenomena]);

  const totalPercentageSum = useMemo(() => {
    return allActualPhenomena.reduce((sum, p) => {
      const { percentage } = calculateSinglePhenomenonCompletion(phenomenaAnswers[p.id]);
      return sum + percentage;
    }, 0);
  }, [allActualPhenomena, phenomenaAnswers]);

  const overallAverageProgress = useMemo(() => {
    return totalPhenomenaCount > 0 ? totalPercentageSum / totalPhenomenaCount : 0;
  }, [totalPhenomenaCount, totalPercentageSum]);

  const completedPhenomenaCount = useMemo(() => {
    return allActualPhenomena.filter(p => {
      const { percentage } = calculateSinglePhenomenonCompletion(phenomenaAnswers[p.id]);
      return percentage >= 70;
    }).length;
  }, [allActualPhenomena, phenomenaAnswers]);


  const currentPhenomenonFlatIndex = allActualPhenomena.findIndex(p => p.id === currentPhenomenonId);
  const nextPhenomenonId = currentPhenomenonFlatIndex < totalPhenomenaCount - 1 ? allActualPhenomena[currentPhenomenonFlatIndex + 1].id : null;
  const prevPhenomenonId = currentPhenomenonFlatIndex > 0 ? allActualPhenomena[currentPhenomenonFlatIndex - 1].id : null;

  const handleNextPhenomenon = () => {
    if (nextPhenomenonId) {
      setCurrentPhenomenonId(nextPhenomenonId);
      if (mainContentRef.current) {
        mainContentRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
  };

  const handlePrevPhenomenon = () => {
    if (prevPhenomenonId) {
      setCurrentPhenomenonId(prevPhenomenonId);
      if (mainContentRef.current) {
        mainContentRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleClearSearch = () => {
    setSearchTerm('');
  };

  const handleAddSuggestedPhenomenon = () => {
    const newId = `new-${nextNewPhenomenonIdCounter}`;
    const newPhenomenon = {
      id: newId,
      name: `پدیده پیشنهادی ${toPersianDigits(nextNewPhenomenonIdCounter)}`,
      type: 'phenomenon',
      mainCategoryName: 'پدیده‌های پیشنهادی شما'
    };
    setUserSuggestedPhenomena(prev => [...prev, newPhenomenon]);
    setNextNewPhenomenonIdCounter(prev => prev + 1);
    setCurrentPhenomenonId(newId);
    setCurrentSection('phenomena');
    if (mainContentRef.current) {
      mainContentRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
    handlePhenomenonAnswerChange(newId, 'phenomenonName', newPhenomenon.name);
  };

  const handleShowFieldExplanation = (field, titleComponent, initialExplanation) => {
    setFieldExplanationModalData({ field, titleComponent, initialExplanation });
    setShowFieldExplanationModal(true);
  };

  const handleSaveFieldExplanation = (explanationText) => {
    if (currentPhenomenonId && fieldExplanationModalData) {
      handlePhenomenonAnswerChange(currentPhenomenonId, `${fieldExplanationModalData.field}Explanation`, explanationText);
    }
    setShowFieldExplanationModal(false);
  };

  const handleShowInterdependencyExplanationInput = (currentPhenomenonName, selectedInterdependencyNames, initialExplanation) => {
    setInterdependencyExplanationInputModalData({
      currentPhenomenonName,
      selectedInterdependencyNames,
      initialExplanation
    });
    setShowInterdependencyExplanationInputModal(true);
  };

  const handleSaveInterdependencyExplanation = (explanationText) => {
    if (currentPhenomenonId) {
      handlePhenomenonAnswerChange(currentPhenomenonId, 'interdependencyExplanation', explanationText);
    }
    setShowInterdependencyExplanationInputModal(false);
  };

  const handleCloseInterdependencyExplanationInputModal = () => {
    setShowInterdependencyExplanationInputModal(false);
  };


  if (loginState === 'initial') {
    return (
      <>
        <LoginScreen
          onLoginSuccess={(id) => {
            setNationalId(id);
            setLoginState('mainApp');
            setCurrentSection('phenomena');
          }}
          onNewUser={(id) => {
            setNationalId(id);
            setLoginState('expertForm');
            setExpertInfo(prev => ({ ...prev, nationalId: id }));
          }}
        />
        {showWelcomeModal && (
          <WelcomeModal
            onClose={() => setShowWelcomeModal(false)}
            message={welcomeMessage}
          />
        )}
      </>
    );
  }

  if (loginState === 'expertForm') {
    return (
      <div className="min-h-screen bg-gray-100 font-inter text-gray-800 flex flex-col items-center justify-center p-4" dir="rtl">
        <ExpertInfoForm
          expertInfo={expertInfo}
          onExpertInfoChange={handleExpertInfoChange}
          nationalId={nationalId}
          onSaveExpertInfo={handleSaveExpertInfo}
          isNewUser={loginState === 'expertForm'}
        />
        {showWelcomeModal && (
          <WelcomeModal
            onClose={() => setShowWelcomeModal(false)}
            message={welcomeMessage}
          />
        )}
      </div>
    );
  }


  return (
    <div className="min-h-screen bg-gray-100 font-inter text-gray-800 flex flex-col" dir="rtl">
      <header className="bg-blue-700 text-white p-4 shadow-md flex justify-between items-center rounded-b-lg">
        <h1 className="text-2xl font-bold flex-grow text-center">سیستم هوشمند تحلیل پدیده‌های چشم‌اندازی</h1>
        <div className="flex items-center space-x-4">
          {nationalId && <span className="text-sm">شناسه کاربر: {toPersianDigits(nationalId)}</span>}
          <button
            onClick={() => setShowGuide(true)}
            className="bg-blue-600 hover:bg-blue-800 text-white font-semibold py-2 px-4 rounded-lg shadow-md transition duration-300 ease-in-out transform hover:scale-105"
          >
            <Info className="inline-block ml-2" size={20} />
            راه‌نما
          </button>
          <button
            onClick={handleSendAll}
            className="bg-green-600 hover:bg-green-700 text-white font-semibold py-2 px-4 rounded-lg shadow-md transition duration-300 ease-in-out transform hover:scale-105"
          >
            <Send className="inline-block ml-2" size={20} />
            ارسال
          </button>
        </div>
      </header>

      <div className="w-full bg-gray-200 h-3 rounded-full mt-4 px-4 mx-auto max-w-7xl flex items-center">
        <div
          className="bg-blue-500 h-full rounded-full transition-all duration-500 ease-in-out"
          style={{ width: `${overallAverageProgress}%` }}
        ></div>
        <div className="text-sm text-gray-600 text-center flex-grow">
          {`پیشرفت کلی: ${toPersianDigits(overallAverageProgress.toFixed(0))}% (${toPersianDigits(completedPhenomenaCount)} از ${toPersianDigits(totalPhenomenaCount)} پدیده تکمیل شده)`}
        </div>
      </div>

      <div className="flex flex-1 mt-4 max-w-7xl mx-auto w-full flex-col md:flex-row p-4 md:p-0">
        <aside className="w-full md:w-1/4 bg-white p-4 rounded-lg shadow-md mb-4 md:mb-0 md:ml-4">
          <h2 className="text-xl font-semibold mb-4 text-center">فهرست</h2>
          <div className="mb-4 relative">
            <input
              id="phenomena-search" type="text" placeholder="جستجوی پدیده..."
              className="w-full p-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500 text-right pl-8"
              value={searchTerm} onChange={handleSearchChange} onKeyDown={(e) => { if (e.key === 'Escape') handleClearSearch(); }}
            />
            {searchTerm && (
              <button
                onClick={handleClearSearch}
                className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 p-1 rounded-full"
                aria-label="پاک کردن جستجو"
              >
                <XCircle size={18} />
              </button>
            )}
          </div>
          <nav className="flex-1 custom-scrollbar">
            <ul>
              <li className="mb-2">
                <button
                  onClick={() => setCurrentSection('expert')}
                  className={`w-full text-right p-3 rounded-lg flex items-center transition duration-200 ${
                    currentSection === 'expert' ? 'bg-blue-100 text-blue-700 font-semibold' : 'hover:bg-gray-50'
                  }`}
                >
                  <User className="ml-2" size={18} />
                  اطلاعات کارشناس
                </button>
              </li>
              <li className="mb-2">
                <button
                  onClick={() => {
                    setCurrentSection('phenomena');
                    if (!currentPhenomenonId && combinedPhenomena.length > 0) {
                      const firstPhenomenon = combinedPhenomena.find(p => p.type === 'phenomenon');
                      if (firstPhenomenon) { setCurrentPhenomenonId(firstPhenomenon.id); }
                    }
                    if (mainContentRef.current) {
                      mainContentRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    }
                  }}
                  className={`w-full text-right p-3 rounded-lg flex items-center transition duration-200 ${
                    currentSection === 'phenomena' ? 'bg-blue-100 text-blue-700 font-semibold' : 'hover:bg-gray-50'
                  }`}
                >
                  <ListTodo className="ml-2" size={18} />
                  تحلیل پدیده‌ها
                  <span className="mr-auto text-sm font-bold" style={{ color: overallAverageProgress <= 30 ? 'red' : overallAverageProgress <= 70 ? 'orange' : 'green' }}>
                    {toPersianDigits(overallAverageProgress.toFixed(0))}%
                  </span>
                </button>
              </li>
              {currentSection === 'phenomena' && (
                <div className="mt-4 pt-4">
                  <ul>
                  {filteredPhenomenaData.map((item) => {
                      const idToCheck = item.type === 'main_category_header' ? `header-${item.id.split('-')[1]}` : item.id;
                      const isSeparatorNeeded = separatorBeforeIds.has(idToCheck);
                      const { percentage } = calculateSinglePhenomenonCompletion(phenomenaAnswers[item.id]);
                      const progressColor = percentage <= 30 ? 'text-red-500' : percentage <= 70 ? 'text-orange-500' : 'text-green-600';
                      const isCompleted70Percent = percentage >= 70;
                      const isSuggested = typeof item.id === 'string' && item.id.startsWith('new-');

                      return (
                        <React.Fragment key={item.id}>
                          {isSeparatorNeeded && (<li className="my-4 border-t border-gray-300 pt-4"></li>)}
                          {item.type === 'main_category_header' ? (
                            <li className="mb-1">
                              <span className="block text-right font-bold text-gray-800 px-2 py-1">{item.name}</span>
                            </li>
                          ) : isSuggested ? (
                            <li className="mb-1">
                              <button
                                onClick={() => {
                                  setCurrentPhenomenonId(item.id);
                                  if (mainContentRef.current) {
                                    mainContentRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
                                  }
                                }}
                                className={`w-full text-right p-2 rounded-lg text-sm transition duration-200 flex justify-between items-center group ${
                                  currentPhenomenonId === item.id ? 'bg-blue-200 text-blue-800 font-medium' : 'hover:bg-gray-100'
                                }`}
                              >
                                <div className="flex items-center flex-grow">
                                  <input
                                    type="text"
                                    value={item.name}
                                    onChange={(e) => {
                                      e.stopPropagation();
                                      handleUpdateSuggestedPhenomenonName(item.id, e.target.value);
                                    }}
                                    onClick={(e) => e.stopPropagation()}
                                    className="flex-grow bg-transparent border-b border-gray-400 focus:outline-none focus:border-blue-500"
                                    placeholder="نام پدیده..."
                                  />
                                </div>
                                <div className="flex items-center ml-2">
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleDeleteSuggestedPhenomenon(item.id);
                                    }}
                                    className="text-red-500 hover:text-red-700 p-1 rounded-full"
                                    title="حذف پدیده"
                                  >
                                    <Trash2 size={16} />
                                  </button>
                                </div>
                              </button>
                            </li>
                          ) : (
                            <li className="mb-1">
                              <button
                                onClick={() => {
                                  setCurrentPhenomenonId(item.id);
                                  if (mainContentRef.current) {
                                    mainContentRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
                                  }
                                }}
                                className={`w-full text-right p-2 rounded-lg text-sm transition duration-200 flex justify-between items-center group ${
                                  currentPhenomenonId === item.id ? 'bg-blue-200 text-blue-800 font-medium' : 'hover:bg-gray-100'
                                }`}
                              >
                                <div className="flex items-center">
                                  <span>{item.name}</span>
                                </div>
                                <span className={`flex items-center text-xs ${progressColor}`}>
                                    {isCompleted70Percent && <CheckCircle className={`ml-1 text-green-500`} size={14} />}
                                    <span className="ml-1">{toPersianDigits(percentage.toFixed(0))}%</span>
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setSelectedPhenomenonForExplanation(item);
                                        setShowExplanationModal(true);
                                      }}
                                      className="ml-2 bg-blue-100 text-blue-700 rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold transition-opacity duration-200"
                                      title="توضیحات پدیده"
                                    >i</button>
                                  </span>
                              </button>
                            </li>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </ul>
                  <div className="mt-4 pt-4 border-t border-gray-300">
                    <button
                      onClick={handleAddSuggestedPhenomenon}
                      className="w-full bg-blue-500 hover:bg-blue-600 text-white font-semibold py-2 px-4 rounded-lg shadow-md transition duration-300 ease-in-out transform hover:scale-105 flex items-center justify-center"
                    >
                      <PlusCircle className="ml-2" size={20} />
                      افزودن پدیده پیشنهادی جدید
                    </button>
                  </div>
                </div>
              )}
            </ul>
          </nav>
        </aside>

        <main ref={mainContentRef} className="flex-1 bg-white p-6 rounded-lg shadow-md mb-4 md:mb-0">
          {currentSection === 'expert' && (
            <ExpertInfoForm
              expertInfo={expertInfo}
              onExpertInfoChange={handleExpertInfoChange}
              nationalId={nationalId}
              onSaveExpertInfo={handleSaveExpertInfo}
              isNewUser={loginState === 'expertForm'}
            />
          )}

          {currentSection === 'phenomena' && currentPhenomenonId && (
            <PhenomenonForm
              phenomenon={combinedPhenomena.find(p => p.id === currentPhenomenonId)}
              answer={phenomenaAnswers[currentPhenomenonId] || {}}
              onAnswerChange={(field, value) => handlePhenomenonAnswerChange(currentPhenomenonId, field, value)}
              allPhenomena={allActualPhenomena}
              onSave={() => {
                const currentPhenomenon = combinedPhenomena.find(p => p.id === currentPhenomenonId);
                toast.success(`پدیده "${currentPhenomenon.name}" ذخیره شد.`);
              }}
              onSaveAndNext={handleNextPhenomenon}
              hasNext={nextPhenomenonId !== null}
              hasPrev={prevPhenomenonId !== null}
              onPrev={handlePrevPhenomenon}
              onShowExplanation={() => {
                setSelectedPhenomenonForExplanation(combinedPhenomena.find(p => p.id === currentPhenomenonId && p.type === 'phenomenon'));
                setShowExplanationModal(true);
              }}
              onShowFieldExplanation={handleShowFieldExplanation}
              onShowInterdependencyExplanationInput={handleShowInterdependencyExplanationInput}
              completionData={calculateSinglePhenomenonCompletion(phenomenaAnswers[currentPhenomenonId])}
            />
          )}

          {currentSection === 'phenomena' && !currentPhenomenonId && combinedPhenomena.length === 0 && (
            <div className="text-center text-gray-500">در حال بارگذاری پدیده‌ها...</div>
          )}
          {currentSection === 'phenomena' && !nationalId && (
            <div className="text-center text-blue-500">در حال آماده‌سازی شناسه کاربر...</div>
          )}
        </main>
      </div>

      {showGuide && (
        <GuideModal onClose={() => setShowGuide(false)} />
      )}

      {showConfirmSendModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white p-8 rounded-lg shadow-xl max-w-md w-full text-center" dir="rtl">
            <h3 className="text-xl font-bold mb-4 text-blue-700">تأیید ارسال اطلاعات</h3>
            <p className="mb-6 text-gray-700">آیا از ارسال نهایی تمامی اطلاعات وارد شده اطمینان دارید؟</p>
            <div className="flex justify-center space-x-4 space-x-reverse">
              <button
                onClick={confirmSend}
                className="bg-green-600 hover:bg-green-700 text-white font-semibold py-2 px-6 rounded-lg shadow-md transition duration-300 ease-in-out"
              >
                بله، ارسال کن
              </button>
              <button
                onClick={cancelSend}
                className="bg-gray-300 hover:bg-gray-400 text-gray-800 font-semibold py-2 px-6 rounded-lg shadow-md transition duration-300 ease-in-out"
              >
                خیر، لغو کن
              </button>
            </div>
          </div>
        </div>
      )}

      {showExplanationModal && selectedPhenomenonForExplanation && (
        <ExplanationModal
          phenomenon={selectedPhenomenonForExplanation}
          onClose={() => setShowExplanationModal(false)}
        />
      )}

      {showFieldExplanationModal && fieldExplanationModalData && (
        <FieldExplanationModal
          titleComponent={fieldExplanationModalData.titleComponent}
          initialExplanation={fieldExplanationModalData.initialExplanation}
          onSave={handleSaveFieldExplanation}
          onClose={() => setShowFieldExplanationModal(false)}
        />
      )}

      {showInterdependencyExplanationInputModal && interdependencyExplanationInputModalData && (
        <InterdependencyExplanationInputModal
          currentPhenomenonName={interdependencyExplanationInputModalData.currentPhenomenonName}
          selectedInterdependencyNames={interdependencyExplanationInputModalData.selectedInterdependencyNames}
          initialExplanation={interdependencyExplanationInputModalData.initialExplanation}
          onSave={handleSaveInterdependencyExplanation}
          onClose={handleCloseInterdependencyExplanationInputModal}
        />
      )}

      <ToastContainer
        position="top-center"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={true}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
    </div>
  );
}

export default App;
